import type { MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getVehicleCacheKey } from '@/constants/cache-keys';
import { EventFilterView } from '@/features/EventFilterView';
import { useVehicleEventsData } from '@/features/EventsListLayout/hooks';
import { EngineService } from '@/services/engine';
import { useDeletedItems, useOpenInNewWindow } from '@/hooks';
import { hasRouterHistory } from '@/stores/navigation-history.store';
import { EVENTS_CONFIG } from '../constants';
import type { EventsPageScenario } from '../types';
import { useEventsPageFilters } from '../useEventsPageFilters';
import { calculateSourceCount } from './calculateSourceCount';

export function useVehicleEventsScenario(): EventsPageScenario {
    const { t } = useTranslation([
        I18nNamespace.COMMON,
        I18nNamespace.EVENTS,
        I18nNamespace.SOURCES,
    ]);
    const { sourceId, vehicleId, hasSourceFilter, setSearchParams } =
        useEventsPageFilters();
    const { openEvent } = useOpenInNewWindow();

    const { data: vehicle, isLoading: vehicleLoading } = useSWR(
        vehicleId ? getVehicleCacheKey(vehicleId) : null,
        () => EngineService.getVehicle(vehicleId ?? ''),
    );

    const eventsResult = useVehicleEventsData({ vehicleId: vehicleId ?? '' });

    const items = useMemo(
        () =>
            eventsResult.occurrences.map((occurrence) => ({
                event: occurrence.event,
                confidence:
                    occurrence.confidence != null
                        ? occurrence.confidence * 100
                        : undefined,
            })),
        [eventsResult.occurrences],
    );

    const onEventClick = useCallback(
        (clickedEventId: string, event?: MouseEvent) => {
            openEvent(clickedEventId, event, {
                state: {
                    from: 'vehicle-events',
                    feedContext: {
                        type: 'vehicle',
                        vehicleId: vehicleId ?? '',
                    },
                },
            });
        },
        [openEvent, vehicleId],
    );

    const { filteredItems, deletedCount } = useDeletedItems({
        items,
        getItemId: (item) => item.event.id,
        deleteItemsFn: (ids) => EngineService.deleteEvents(ids),
        successMessage: t('actions.deleteSuccess'),
        errorMessage: t('actions.deleteError'),
    });

    const sourceCount = useMemo(
        () => calculateSourceCount(filteredItems, eventsResult, sourceId),
        [
            filteredItems,
            eventsResult.totalItems,
            eventsResult.pageSize,
            sourceId,
        ],
    );

    const handleClearVehicle = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('vehicleId');
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    return {
        pageKey: `vehicle-events-${vehicleId}`,
        items: filteredItems,
        eventCount: eventsResult.totalItems
            ? eventsResult.totalItems - deletedCount
            : filteredItems.length,
        error: eventsResult.error,
        isLoading:
            eventsResult.isLoading || (vehicleId ? vehicleLoading : false),
        pagination: {
            mode: 'paginated',
            page: eventsResult.page,
            pageSize: eventsResult.pageSize,
            totalCount: eventsResult.totalItems,
            isFetching: eventsResult.isFetching,
            onPageChange: eventsResult.onPageChange,
        },
        isEmpty: filteredItems.length === 0,
        sourceCount,
        emptyMessage: t('events:page.noResults'),
        showBackButton: !!vehicleId || hasSourceFilter || hasRouterHistory(),
        backButtonLabel: t('page.backToVehicle'),
        filterView:
            hasSourceFilter || !!vehicleId ? (
                <EventFilterView
                    config={EVENTS_CONFIG}
                    vehicleId={vehicleId}
                    vehicle={vehicle}
                    onClearVehicle={vehicleId ? handleClearVehicle : undefined}
                />
            ) : undefined,
        onEventClick,
    };
}
